import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button, CircularProgress, Container, Paper, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { AppHelper } from "../../AppHelper";
import AppTopBar from "../common/app-top-bar";

const ROUND_SIZE = 5;
const GAME_GRADES = ["P3", "P4", "P5", "P6"];

// prompt uses ____ for the missing word. kind: 'vocabulary' | 'spelling'. Content is AI-drafted; have a teacher review it.
const WORDS = {
  P4: [
    { kind: "vocabulary", prompt: "The soup was so ____ that I asked for a second bowl.", answer: "delicious", wrong: ["hungry", "noisy", "narrow"] },
    { kind: "vocabulary", prompt: "\"I am scared,\" Mei ____ so that no one could hear.", answer: "whispered", wrong: ["shouted", "laughed", "sang"] },
    { kind: "vocabulary", prompt: "Dad ____ up the stairs after a long day at work.", answer: "trudged", wrong: ["floated", "melted", "sparkled"] },
    { kind: "vocabulary", prompt: "The ice cream melted because the day was ____.", answer: "scorching", wrong: ["freezing", "gloomy", "silent"] },
    { kind: "vocabulary", prompt: "Ali ____ at the clock and saw that he was late.", answer: "glanced", wrong: ["grabbed", "galloped", "gathered"] },
    { kind: "vocabulary", prompt: "The puppy was ____ when its owner came home.", answer: "delighted", wrong: ["frightened", "exhausted", "puzzled"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "wonderful", wrong: ["wonderfull", "wunderful", "wonderfal"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "careful", wrong: ["carefull", "carful", "carefal"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "library", wrong: ["libary", "liberry", "librery"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "mathematics", wrong: ["mathamatics", "mathmatics", "matematics"] },
  ],
  P5: [
    { kind: "vocabulary", prompt: "Mum was ____ with my results.", answer: "delighted", wrong: ["delicate", "defeated", "deserted"] },
    { kind: "vocabulary", prompt: "The hikers were ____ after climbing for six hours.", answer: "exhausted", wrong: ["refreshed", "cheerful", "generous"] },
    { kind: "vocabulary", prompt: "The thief ____ down the alley when he heard the siren.", answer: "sprinted", wrong: ["strolled", "crawled", "wandered"] },
    { kind: "vocabulary", prompt: "Mr Lim was ____ when he saw the mess in the classroom.", answer: "furious", wrong: ["curious", "generous", "joyful"] },
    { kind: "vocabulary", prompt: "\"Stop!\" the guard ____.", answer: "shouted", wrong: ["whispered", "hummed", "mumbled"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "embarrassing", wrong: ["embarassing", "embarrasing", "embaressing"] },
    { kind: "spelling", prompt: "I bought some ____ for school. (pens, paper, rulers)", answer: "stationery", wrong: ["stationary", "stationry", "stasionery"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "performance", wrong: ["performence", "perfomance", "performanse"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "announced", wrong: ["anounced", "annonced", "annouced"] },
    { kind: "spelling", prompt: "Which word is spelt correctly?", answer: "received", wrong: ["recieved", "receeved", "reseived"] },
  ],
};

function shuffle(list) {
  const a = list.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Lenient comparison for typed answers: ignores case, extra spaces, curly quotes and spacing before punctuation.
function normaliseText(s) {
  return String(s)
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+([.,!?;:])/g, "$1")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}
function sameText(a, b) {
  return normaliseText(a) === normaliseText(b);
}

function buildRound(grade, skill) {
  const bank = WORDS[grade] || WORDS.P5;
  const preferred = skill ? bank.filter((w) => w.kind === skill) : bank;
  const picked = shuffle(preferred.length >= ROUND_SIZE ? preferred : bank).slice(0, ROUND_SIZE);
  return picked.map((w) => ({ format: "choice", prompt: w.prompt, answer: w.answer, options: shuffle([w.answer, ...w.wrong]) }));
}

export default function PageWordSafari() {
  const navigate = useNavigate();
  const location = useLocation();
  const oUser = AppHelper.userCacheGet();
  const refTopBar = useRef(null);
  const params = new URLSearchParams(window.location.search);
  const initialGrade = params.get("grade") || (oUser && oUser.grade) || "P5";
  const [grade, setGrade] = useState(GAME_GRADES.includes(initialGrade) ? initialGrade : "P5");
  const skill = params.get("skill") || "";
  const essayErrors = (location.state && location.state.errors) || [];
  const essayGrade = useRef(initialGrade);

  const [round, setRound] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roundKey, setRoundKey] = useState(0);
  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState("");
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState(null);
  const [correct, setCorrect] = useState(0);
  const [hintUsed, setHintUsed] = useState(false);
  const [hints, setHints] = useState(0);
  const [hintText, setHintText] = useState("");
  const [hiddenOptions, setHiddenOptions] = useState([]);

  useEffect(() => {
    if (!oUser) navigate("/login");
  }, []);

  // New questions are fetched for every round so a learner does not keep seeing the same ones.
  // The built-in word bank is only a fallback if the server cannot supply any.
  useEffect(() => {
    if (!oUser) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let items = [];
      try {
        const res = await AppHelper.apiPost("api/practice/next", {
          token: oUser.token, grade, skill, count: ROUND_SIZE,
          errors: roundKey === 0 && grade === essayGrade.current ? essayErrors : [],
        });
        if (res && res.status && Array.isArray(res.items)) items = res.items;
      } catch (e) {
        items = [];
      }
      if (items.length === 0) items = buildRound(grade, skill);
      if (!cancelled) {
        setRound(items);
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roundKey, grade]);

  if (!oUser) return null;

  const finished = !loading && index >= round.length;
  const q = round[index];
  const stars = correct >= round.length && round.length > 0 ? 3 : correct >= Math.ceil(round.length * 0.6) ? 2 : correct >= 1 ? 1 : 0;
  const answered = picked !== "" || result !== null;

  const onPick = (opt) => {
    if (answered) return;
    setPicked(opt);
    if (opt === q.answer) setCorrect((c) => c + 1);
  };
  const onCheck = () => {
    if (answered || !typed.trim()) return;
    const ok = sameText(typed, q.answer);
    setResult(ok ? "right" : "wrong");
    if (ok) setCorrect((c) => c + 1);
  };
  const onHint = () => {
    if (answered || hintUsed) return;
    if (q.format === "choice") {
      setHiddenOptions(shuffle(q.options.filter((o) => o !== q.answer)).slice(0, 2));
    } else {
      setHintText(q.answer.slice(0, Math.max(3, Math.ceil(q.answer.length / 3))) + "...");
    }
    setHintUsed(true);
    setHints((h) => h + 1);
  };
  const resetQuestion = () => {
    setPicked("");
    setTyped("");
    setResult(null);
    setHintUsed(false);
    setHintText("");
    setHiddenOptions([]);
  };
  const onNext = () => {
    setIndex((i) => i + 1);
    resetQuestion();
  };
  const onAgain = () => {
    setIndex(0);
    setCorrect(0);
    setHints(0);
    resetQuestion();
    setRoundKey((k) => k + 1);
  };

  const onGradeChange = (e, g) => {
    if (!g || g === grade) return;
    setIndex(0);
    setCorrect(0);
    setHints(0);
    resetQuestion();
    setRound([]);
    setLoading(true);
    setGrade(g);
  };

  const wasRight = picked ? picked === q.answer : result === "right";

  return (
    <div>
      <AppTopBar ref={refTopBar} />
      <Container component="main" maxWidth="sm" sx={{ mb: 4 }}>
        <Paper sx={{ my: { xs: 3, md: 5 }, p: { xs: 2, md: 3 } }} elevation={3}>
          <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 1 }}>
            Word Safari
          </Typography>
          <Box display="flex" alignItems="center" gap={1} sx={{ marginBottom: 2 }}>
            <Typography variant="body2">Level:</Typography>
            <ToggleButtonGroup size="small" exclusive color="success" value={grade} onChange={onGradeChange}>
              {GAME_GRADES.map((g) => (
                <ToggleButton key={g} value={g} disabled={loading}>
                  {g}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {loading && (
            <Box display="flex" alignItems="center" gap={2} sx={{ marginY: 3 }}>
              <CircularProgress size={28} />
              <Typography>Finding new questions for you...</Typography>
            </Box>
          )}

          {!loading && !finished && q && (
            <>
              <Typography variant="body2" sx={{ marginBottom: 1 }}>
                Question {index + 1} of {round.length}
              </Typography>
              <Typography variant="h6" sx={{ marginBottom: 2 }}>
                {q.prompt}
              </Typography>

              {q.format === "choice" && (
                <Box display="flex" flexDirection="column" gap={1}>
                  {q.options
                    .filter((o) => !hiddenOptions.includes(o))
                    .map((opt) => {
                      const isAnswer = picked && opt === q.answer;
                      const isWrongPick = picked && opt === picked && opt !== q.answer;
                      return (
                        <Button
                          key={opt}
                          variant={picked ? "contained" : "outlined"}
                          color={isAnswer ? "success" : isWrongPick ? "error" : "primary"}
                          disabled={!!picked && !isAnswer && !isWrongPick}
                          onClick={() => onPick(opt)}
                          sx={{ textTransform: "none", fontSize: "1.1rem" }}
                        >
                          {opt}
                        </Button>
                      );
                    })}
                </Box>
              )}

              {q.format === "fixit" && (
                <>
                  <Paper variant="outlined" sx={{ padding: 1.5, marginBottom: 2, backgroundColor: "#fff8e1" }}>
                    <Typography sx={{ fontSize: "1.1rem" }}>{q.text}</Typography>
                  </Paper>
                  <TextField
                    fullWidth
                    multiline
                    label="Type the correct version"
                    value={typed}
                    disabled={answered}
                    onChange={(e) => setTyped(e.target.value)}
                  />
                  {hintText && <Typography sx={{ marginTop: 1 }}>Hint: {hintText}</Typography>}
                </>
              )}

              {answered && (
                <Typography sx={{ marginTop: 2 }}>
                  {wasRight ? "Correct!" : `Not quite. The answer is "${q.answer}".`}
                </Typography>
              )}
              <Box sx={{ marginTop: 2 }} display="flex" gap={1}>
                {!answered && q.format === "fixit" && (
                  <Button variant="contained" color="success" onClick={onCheck} disabled={!typed.trim()}>
                    Check
                  </Button>
                )}
                {!answered && (
                  <Button onClick={onHint} disabled={hintUsed}>
                    {q.format === "choice" ? "Hint (remove 2 choices)" : "Hint"}
                  </Button>
                )}
                {answered && (
                  <Button variant="contained" color="success" onClick={onNext}>
                    {index + 1 >= round.length ? "See my stars" : "Next"}
                  </Button>
                )}
              </Box>
            </>
          )}

          {finished && (
            <>
              <Box display="flex" justifyContent="center" sx={{ marginY: 2 }}>
                {[1, 2, 3].map((n) =>
                  n <= stars ? (
                    <StarIcon key={n} sx={{ fontSize: 56, color: "#f5b301" }} />
                  ) : (
                    <StarBorderIcon key={n} sx={{ fontSize: 56, color: "#bbb" }} />
                  )
                )}
              </Box>
              <Typography align="center" variant="h6">
                You got {correct} out of {round.length}.
              </Typography>
              <Typography align="center" variant="body2" sx={{ marginBottom: 2 }}>
                Hints used: {hints}. Play again for brand new questions!
              </Typography>
              <Box display="flex" justifyContent="center" gap={1}>
                <Button variant="contained" color="success" onClick={onAgain}>
                  Play again
                </Button>
                <Button variant="outlined" onClick={() => navigate("/home")}>
                  Back home
                </Button>
              </Box>
            </>
          )}
        </Paper>
      </Container>
    </div>
  );
}
